import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { X, Sprout } from "lucide-react";
import { toast } from "sonner";
import { getPhenologyPhases } from "../pages/Dashboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FertilizationForm({ vineyardId, onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    product_name: "",
    fertilization_date: new Date().toISOString().slice(0, 10),
    product_type: "",
    quantity_used: "",
    surface_treated: "",
    operator_name: "",
    application_method: "",
    objective: "",
    phenology_phase: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_name || !form.fertilization_date) {
      toast.error(t("treatment.missingRequired"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        vineyard_id: vineyardId,
        product_name: form.product_name,
        fertilization_date: form.fertilization_date,
        product_type: form.product_type || null,
        quantity_used: form.quantity_used || null,
        surface_treated: form.surface_treated ? parseFloat(form.surface_treated) : null,
        operator_name: form.operator_name || null,
        application_method: form.application_method || null,
        objective: form.objective || null,
        phenology_phase: form.phenology_phase || null,
        notes: form.notes || null,
      };
      const r = await axios.post(`${API}/fertilizations`, payload, { withCredentials: true });
      toast.success(t("fertilizationForm.successSaved"));
      onCreated(r.data);
    } catch { toast.error(t("treatment.saveError")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(17,20,17,0.5)" }} onClick={() => !saving && onClose()}>
      <div className="w-full sm:max-w-lg vs-card p-5"
        style={{ borderRadius: "16px 16px 0 0", maxHeight: "92vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()} data-testid="fertilization-form">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sprout size={22} style={{ color: "var(--primary)" }} />
            <h3 className="heading text-xl font-bold">{t("fertilizationForm.title")}</h3>
          </div>
          <button onClick={onClose} className="p-2"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="vs-label">{t("fertilizationForm.productLabel")}</label>
            <input className="vs-input" value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              placeholder={t("fertilizationForm.productPlaceholder")} data-testid="fertform-name" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vs-label">{t("fertilizationForm.dateLabel")}</label>
              <input type="date" className="vs-input" value={form.fertilization_date}
                onChange={(e) => setForm({ ...form, fertilization_date: e.target.value })}
                data-testid="fertform-date" required />
            </div>
            <div>
              <label className="vs-label">{t("fertilizationForm.typeLabel")}</label>
              <select className="vs-input" value={form.product_type}
                onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                data-testid="fertform-type">
                <option value="">—</option>
                {t("fertilizationForm.productTypes", { returnObjects: true }).map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="vs-label">{t("fertilizationForm.quantityLabel")}</label>
              <input className="vs-input" value={form.quantity_used}
                onChange={(e) => setForm({ ...form, quantity_used: e.target.value })}
                placeholder={t("fertilizationForm.quantityPlaceholder")} data-testid="fertform-qty" />
            </div>
            <div>
              <label className="vs-label">{t("treatment.surfaceLabel")}</label>
              <input type="number" step="0.01" className="vs-input" value={form.surface_treated}
                onChange={(e) => setForm({ ...form, surface_treated: e.target.value })}
                placeholder="0.8" data-testid="fertform-surface" />
            </div>
          </div>
          <div>
            <label className="vs-label">{t("fertilizationForm.methodLabel")}</label>
            <select className="vs-input" value={form.application_method}
              onChange={(e) => setForm({ ...form, application_method: e.target.value })}
              data-testid="fertform-method">
              <option value="">—</option>
              {t("fertilizationForm.applicationMethods", { returnObjects: true }).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="vs-label">{t("fertilizationForm.objectiveLabel")}</label>
            <select className="vs-input" value={form.objective}
              onChange={(e) => setForm({ ...form, objective: e.target.value })}
              data-testid="fertform-objective">
              <option value="">—</option>
              {t("fertilizationForm.objectives", { returnObjects: true }).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="vs-label">{t("fertilizationForm.operatorLabel")}</label>
            <input className="vs-input" value={form.operator_name}
              onChange={(e) => setForm({ ...form, operator_name: e.target.value })}
              placeholder={t("treatment.operatorPlaceholder")} data-testid="fertform-operator" />
          </div>
          <div>
            <label className="vs-label">{t("dashboard.form.phenologyLabel")}</label>
            <select className="vs-input" value={form.phenology_phase}
              onChange={(e) => setForm({ ...form, phenology_phase: e.target.value })}
              data-testid="fertform-phase">
              <option value="">—</option>
              {getPhenologyPhases(t).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="vs-label">{t("treatment.notesLabel")}</label>
            <textarea className="vs-input" rows="2" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder={t("fertilizationForm.notesPlaceholder")}
              data-testid="fertform-notes" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" className="vs-btn-secondary flex-1" onClick={onClose} disabled={saving}>{t("dashboard.form.cancelButton")}</button>
            <button type="submit" className="vs-btn-primary flex-1" disabled={saving}
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
              data-testid="fertform-submit">
              {saving ? t("dashboard.form.submitSaving") : t("fertilizationForm.submitButton")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
